<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/api'
import { useExcelExport } from '@/composables/export'

const { t } = useI18n()

const props = defineProps({
  modelName: {
    type: String,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  listName: {
    type: String,
    default: '',
  },
})

const listConfigModel = ref()
const listName = computed(() => props.listName || props.modelName)

const { refresh: fetchListConfig } = useApi({
  url: computed(() => `list_configs/my/${listName.value}`),
  model: listConfigModel,
})
function getVisibleColumns(cfg) {
  if (!cfg || typeof cfg !== 'object') return props.columns

  const hiddenSet = new Set()
  const byTitle = Object.fromEntries(props.columns.map(c => [c.title, c]))

  Object.keys(cfg).forEach(key => {
    if (key === '_order') return
    if (cfg[key]?.hidden) hiddenSet.add(key)
  })

  if (Array.isArray(cfg._order)) {
    const ordered = cfg._order.filter(t => !hiddenSet.has(t) && byTitle[t]).map(t => byTitle[t])
    const rest = props.columns.filter(c => !cfg._order.includes(c.title) && !hiddenSet.has(c.title))
    return [...ordered, ...rest]
  }

  return props.columns.filter(c => !hiddenSet.has(c.title))
}

const pending = ref(false)

async function doExport() {
  await fetchListConfig()
  const cols = getVisibleColumns(listConfigModel.value?.list_config)
  const { exportToExcel } = useExcelExport(
    () => props.modelName,
    () => cols,
    () => props.filters
  )
  pending.value = true
  try {
    await exportToExcel()
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <button
    type="button"
    class="btn btn-secondary ms-auto"
    @click="doExport"
    :disabled="pending"
  >
    <i class="bi bi-filetype-xls" />{{ t('exportToXls') }}
  </button>
</template>

<i18n>
{
  "ru": {
    "exportToXls": "Выгрузить в XLS"
  },
  "en": {
    "exportToXls": "Export to XLS"
  }
}
</i18n>
